import type { OSSchema } from "@sloe/schema";
import type { AssistantProfile } from "./registry";

export type WorkbenchItem = {
  title: string;
  detail: string;
  status?: "ready" | "needs_data" | "missing" | "watching";
  actionPrompt?: string;
};

export type WorkbenchActionReceipt = {
  id?: string;
  tool_called: string;
  claim_summary?: string | null;
  tool_response?: any;
  created_at?: string | null;
};

export type WorkbenchAssignmentRun = {
  id: string;
  instruction: string;
  active?: boolean;
  last_run_at?: string | null;
  last_run_status?: string | null;
  last_run_output?: string | null;
};

export type AgentWorkbench = {
  currentFocus: WorkbenchItem[];
  starterActions: WorkbenchItem[];
  dataNeeds: WorkbenchItem[];
  skills: WorkbenchItem[];
  outputs: WorkbenchItem[];
  failedRuns: WorkbenchItem[];
  recommendedScreens: WorkbenchItem[];
  successMetrics: string[];
};

const SKILL_LABELS: Record<string, string> = {
  workspace_data_read: "Workspace data reader",
  record_operations: "Record operator",
  screen_triage: "Triage analyst",
  outlook_projection: "Projection analyst",
  connector_discovery: "Connector discovery",
  desktop_agent_bridge: "Local access bridge",
  schema_evolution: "Agentic Operating System evolution",
  failure_diagnosis: "Failure diagnosis",
  real_estate_pipeline: "Real estate pipeline",
  real_estate_marketing_pack: "Real estate marketing pack",
  football_scouting: "Football scouting",
  document_generation: "Document generation",
  spreadsheet_analysis: "Spreadsheet analysis",
};

const SKILL_DETAILS: Record<string, string> = {
  document_generation: "Packages briefs, reports, copy, or PDFs from approved workspace facts.",
  spreadsheet_analysis: "Reads sheets/CSVs, validates columns, and maps rows into Agentic Operating System screens.",
  real_estate_pipeline: "Watches listings, deals, documents, stale follow-ups, and next moves.",
  real_estate_marketing_pack: "Turns listings and brand context into reusable campaign assets.",
  football_scouting: "Ranks players, prospects, trials, risks, and scouting next actions.",
  desktop_agent_bridge: "Requests permission through the approved local access path when data lives on the user's laptop.",
};

function firstScreen(schema: OSSchema): string {
  return schema.screens?.[0]?.label || "the workspace";
}

function screenExists(schema: OSSchema, needle: string): boolean {
  const n = needle.toLowerCase();
  return (schema.screens || []).some((screen) =>
    `${screen.screen_id} ${screen.label} ${screen.layout}`.toLowerCase().includes(n),
  );
}

function profileText(profile: AssistantProfile): string {
  return `${profile.id} ${profile.display_name} ${profile.role_description} ${(profile as any).workspace_blueprint?.required_screens?.join(" ") || ""}`.toLowerCase();
}

function contextText(schema: OSSchema, profile: AssistantProfile): string {
  return [
    profileText(profile),
    schema.metadata?.industry_shape || "",
    schema.metadata?.name || "",
    ...(schema.screens || []).flatMap((screen) => [screen.screen_id, screen.label, screen.layout]),
  ].join(" ").toLowerCase();
}

function instructionLabel(instruction: string): string {
  const first = (instruction || "Standing order").split("\n")[0].trim() || "Standing order";
  return first.length > 54 ? `${first.slice(0, 53).trimEnd()}...` : first;
}

function starterActionTitle(task: string): string {
  const clean = task.replace(/\s+/g, " ").trim();
  if (!clean) return "Start a useful run";
  const title = clean.replace(/[.!?]+$/g, "");
  return title.length > 52 ? `${title.slice(0, 51).trimEnd()}...` : title;
}

function defaultStarterActions(profile: AssistantProfile): string[] {
  if (profile.id === "operator") {
    return [
      "Ask where the first real data source lives and connect it with the smallest safe permission.",
      "Create one useful workspace record from a user-provided example.",
      "Triage the workspace and explain the first missing data or permission blocker.",
    ];
  }
  if (profile.id === "analyst") {
    return [
      "Rank the top priorities using real workspace rows and show the evidence behind each one.",
      "Project the next 30 days and state confidence, drivers, and missing assumptions.",
      "Find stale or risky records and turn missing data into the next question.",
    ];
  }
  if (profile.id === "architect") {
    return [
      "Review the Agentic Operating System for missing screens, fields, agents, or permissions.",
      "Create a Decision for the smallest structural improvement that would make repeated work easier.",
      "Explain which specialist agents or skills should be researched before anything new is deployed.",
    ];
  }
  return [
    `Review ${profile.display_name}'s specialty and tell me the first useful action.`,
    "Find the missing data, screen, permission, or skill that blocks this agent from helping.",
    "Run one small task and save any repeatable output back to the workspace.",
  ];
}

function buildStarterActions(profile: AssistantProfile): WorkbenchItem[] {
  const configured = profile.workspace_blueprint?.starter_tasks || [];
  const tasks = (configured.length > 0 ? configured : defaultStarterActions(profile)).slice(0, 4);
  return tasks.map((task) => ({
    title: starterActionTitle(task),
    detail: "Runs through this agent's chat. If the agent is blocked, it must name the missing data, permission, tool, screen, or skill.",
    status: "ready",
    actionPrompt: task,
  }));
}

function classifyFailureDetail(output: string, status: string): string {
  const text = output.trim() || "No run output was recorded.";
  const lower = text.toLowerCase();
  if (status === "skipped" || /quota|rate.?limit|model unavailable|provider|overload|timeout|503|502|429/.test(lower)) {
    return `Model/provider issue: ${text} The next attempt can succeed after capacity returns or the agent switches to an approved fallback model.`;
  }
  if (/permission|unauthorized|forbidden|access|scope|token/.test(lower)) {
    return `Permission missing: ${text} The next attempt needs a connector, token refresh, or approved local access before the agent tries again.`;
  }
  if (/missing|no matching data|not found|empty|no rows|source/.test(lower)) {
    return `Data missing: ${text} The next attempt needs the exact file, app, table, or workspace screen where this data lives.`;
  }
  if (/tool|unsupported|not enabled|connector/.test(lower)) {
    return `Tool gap: ${text} The next attempt needs the right connector, tool, or specialist skill before the run is useful.`;
  }
  return `Run failed: ${text} The agent should diagnose whether the blocker is data, permission, tool access, skill coverage, or provider availability before retrying.`;
}

export function buildFailedRunItems(
  actions: WorkbenchActionReceipt[] | null | undefined,
  assignments: WorkbenchAssignmentRun[] | null | undefined,
): WorkbenchItem[] {
  const assignmentFailures: WorkbenchItem[] = (assignments || [])
    .filter((assignment) =>
      /error|failure|failed|skipped/i.test(String(assignment.last_run_status || "")) ||
      /fail|failed|error|skipped|model unavailable/i.test(String(assignment.last_run_output || "")),
    )
    .slice(0, 3)
    .map((assignment) => {
      const status = String(assignment.last_run_status || "").toLowerCase();
      return {
        title: `Assignment: ${instructionLabel(assignment.instruction)}`,
        detail: classifyFailureDetail(String(assignment.last_run_output || ""), status),
        status: status === "skipped" ? "watching" : "missing",
      };
    });

  const actionFailures: WorkbenchItem[] = (actions || [])
    .filter((action) =>
      action.tool_response?.error ||
      /fail|failed|error/i.test(String(action.claim_summary || "")),
    )
    .slice(0, 3)
    .map((action) => ({
      title: action.tool_called.replace(/[_-]+/g, " "),
      detail: classifyFailureDetail(
        String(action.tool_response?.error || action.claim_summary || "Run failed"),
        "error",
      ),
      status: "missing",
    }));

  return [...assignmentFailures, ...actionFailures].slice(0, 3);
}

function isCreative(schema: OSSchema, profile: AssistantProfile): boolean {
  const text = contextText(schema, profile);
  return /social media|content calendar|creative library|campaign assets|brand kit|copywriter|image|video|visual asset|post|posts|creator/.test(text);
}

function isRealEstate(schema: OSSchema, profile: AssistantProfile): boolean {
  const text = contextText(schema, profile);
  return /real estate|property|properties|listing|buyer|seller|tenant|lease|closing|escrow/.test(text);
}

function isFootball(schema: OSSchema, profile: AssistantProfile): boolean {
  const text = contextText(schema, profile);
  return /football|player|scout|trial|contract|squad|academy/.test(text);
}

function isSales(schema: OSSchema, profile: AssistantProfile): boolean {
  const text = contextText(schema, profile);
  return /sales|lead|leads|deal|deals|prospect|prospects|proposal|proposals|outreach/.test(text);
}

function isFinance(schema: OSSchema, profile: AssistantProfile): boolean {
  const text = contextText(schema, profile);
  return /finance|invoice|invoices|expense|expenses|cash|payment|payments|forecast|stripe|quickbooks/.test(text);
}

function isDelivery(schema: OSSchema, profile: AssistantProfile): boolean {
  const text = contextText(schema, profile);
  return /delivery|project|projects|task|tasks|blocker|blockers|handoff|deadline/.test(text);
}

function isCustomerSuccess(schema: OSSchema, profile: AssistantProfile): boolean {
  const text = contextText(schema, profile);
  return /customer success|account|accounts|ticket|tickets|renewal|renewals|churn|success plan/.test(text);
}

function isRecruiting(schema: OSSchema, profile: AssistantProfile): boolean {
  const text = contextText(schema, profile);
  return /recruit|candidate|candidates|interview|scorecard|scorecards|offer|offers|hiring/.test(text);
}

function isOilGas(schema: OSSchema, profile: AssistantProfile): boolean {
  const text = contextText(schema, profile);
  return /oil|gas|energy|well|wells|hse|permit|permits|maintenance|downtime|field operations|production checks/.test(text);
}

function isPartnerships(schema: OSSchema, profile: AssistantProfile): boolean {
  const text = contextText(schema, profile);
  return /partner|partners|partnership|partnerships|obligation|obligations|expansion/.test(text);
}

function blueprint(profile: AssistantProfile): NonNullable<AssistantProfile["workspace_blueprint"]> {
  return profile.workspace_blueprint || {};
}

export function buildAgentWorkbench(
  profile: AssistantProfile,
  schema: OSSchema,
  countsByScreen: Map<string, number>,
): AgentWorkbench {
  const bp = blueprint(profile);
  const skillIds = profile.skill_ids || [];
  const requiredScreens = bp.required_screens || [];
  const successMetrics = bp.success_metrics || [];

  const schemaCollections = (schema.screens || []).map((screen) => ({
    title: screen.label,
    detail: `${countsByScreen.get(screen.screen_id) || 0} rows · ${screen.layout || "collection"}`,
    status: (countsByScreen.get(screen.screen_id) || 0) > 0 ? "ready" as const : "needs_data" as const,
  }));

  let currentFocus: WorkbenchItem[] = [
    {
      title: `Watch ${firstScreen(schema)}`,
      detail: "Use live rows to answer, triage, and recommend the next move.",
      status: "watching",
    },
  ];
  let dataNeeds: WorkbenchItem[] = schemaCollections.slice(0, 4);
  const starterActions = buildStarterActions(profile);
  let outputs: WorkbenchItem[] = [
    { title: "Useful replies", detail: "Tables, charts, rankings, and timelines shown in chat.", status: "ready" },
    { title: "Saved workspace context", detail: "Repeatable work should become screens, assignments, or Decisions.", status: "watching" },
  ];
  let recommendedScreens: WorkbenchItem[] = requiredScreens.map((label) => ({
    title: label,
    detail: screenExists(schema, label) ? "Already present in the Agentic Operating System." : "Recommended for this agent's repeatable work.",
    status: screenExists(schema, label) ? "ready" : "missing",
  }));

  if (profile.id === "operator") {
    currentFocus = [
      { title: "Move work forward", detail: "Create records directly and propose sensitive updates for approval.", status: "ready" },
      { title: "Find missing data", detail: "Ask where live data lives, then route the user to the right permission flow.", status: "watching" },
    ];
  } else if (profile.id === "analyst") {
    currentFocus = [
      { title: "Triage priorities", detail: "Rank what needs attention with evidence from real rows.", status: "watching" },
      { title: "Project outcomes", detail: "Explain likely direction, drivers, risks, and confidence.", status: "watching" },
    ];
  } else if (profile.id === "architect") {
    currentFocus = [
      { title: "Improve the Agentic Operating System", detail: "Turn repeated friction into fields, screens, assignments, or specialist staff.", status: "watching" },
      { title: "Protect control", detail: "Every structural change lands as a Decision before it applies.", status: "ready" },
    ];
  }

  if (isCreative(schema, profile)) {
    currentFocus = [
      { title: "Campaign production", detail: "Turn briefs, listings, and brand context into usable creative assets.", status: "watching" },
      { title: "Copy & document routing", detail: "Choose copy or document tools based on the asset requested.", status: "ready" },
    ];
    dataNeeds = [
      { title: "Brand kit", detail: "Logo, colors, voice, banned phrases, and reusable visual style.", status: screenExists(schema, "brand") ? "ready" : "missing" },
      { title: "Source assets", detail: "Photos, clips, listing/product facts, and approved claims.", status: screenExists(schema, "asset") ? "ready" : "needs_data" },
      { title: "Campaign brief", detail: "Audience, offer, channel, deadline, and success metric.", status: screenExists(schema, "campaign") ? "ready" : "missing" },
    ];
    outputs = [
      { title: "Campaign copy", detail: "Captions, emails, landing copy, and ad variants.", status: "needs_data" },
    ];
    recommendedScreens = [
      { title: "Creative Library", detail: screenExists(schema, "creative") ? "Already present." : "Stores generated assets so they are not lost in chat.", status: screenExists(schema, "creative") ? "ready" : "missing" },
      { title: "Content Calendar", detail: screenExists(schema, "calendar") ? "Already present." : "Turns repeatable publishing into scheduled work.", status: screenExists(schema, "calendar") ? "ready" : "missing" },
      { title: "Campaign Assets", detail: screenExists(schema, "campaign") ? "Already present." : "Tracks briefs, outputs, approvals, and performance.", status: screenExists(schema, "campaign") ? "ready" : "missing" },
      { title: "Brand Kit", detail: screenExists(schema, "brand") ? "Already present." : "Gives the agent consistent creative constraints.", status: screenExists(schema, "brand") ? "ready" : "missing" },
    ];
  } else if (isRealEstate(schema, profile)) {
    currentFocus = [
      { title: "Listing movement", detail: "Watch stale listings, offers, viewings, docs, and follow-ups.", status: "watching" },
      { title: "Client pipeline", detail: "Separate buyers, sellers, tenants, and deals by next action.", status: "watching" },
    ];
    recommendedScreens = [
      { title: "Listings", detail: screenExists(schema, "listing") ? "Already present." : "Core record surface for property inventory.", status: screenExists(schema, "listing") ? "ready" : "missing" },
      { title: "Deals Pipeline", detail: screenExists(schema, "deal") ? "Already present." : "Tracks inquiry to offer to closing.", status: screenExists(schema, "deal") ? "ready" : "missing" },
      { title: "Documents", detail: screenExists(schema, "document") ? "Already present." : "Tracks contracts, disclosures, and missing paperwork.", status: screenExists(schema, "document") ? "ready" : "missing" },
      { title: "Listing Campaigns", detail: screenExists(schema, "campaign") ? "Already present." : "Stores creative assets and campaign results.", status: screenExists(schema, "campaign") ? "ready" : "missing" },
    ];
  } else if (isFootball(schema, profile)) {
    recommendedScreens = [
      { title: "Players", detail: screenExists(schema, "player") ? "Already present." : "Core scouting and roster surface.", status: screenExists(schema, "player") ? "ready" : "missing" },
      { title: "Scouting Reports", detail: screenExists(schema, "scout") ? "Already present." : "Stores observations and ranking evidence.", status: screenExists(schema, "scout") ? "ready" : "missing" },
      { title: "Contracts", detail: screenExists(schema, "contract") ? "Already present." : "Tracks dates, renewals, and paperwork.", status: screenExists(schema, "contract") ? "ready" : "missing" },
      { title: "Development Plans", detail: screenExists(schema, "development") ? "Already present." : "Turns player growth into repeatable follow-up.", status: screenExists(schema, "development") ? "ready" : "missing" },
    ];
  } else if (isOilGas(schema, profile)) {
    currentFocus = [
      { title: "Field priority", detail: "Rank wells, maintenance, downtime, inspections, and HSE evidence by operational risk.", status: "watching" },
      { title: "Safety control", detail: "Route permits, incidents, and corrective actions through auditable approval paths.", status: "ready" },
    ];
    dataNeeds = [
      { title: "Wells", detail: "Production status, last check, owner, and risk notes.", status: screenExists(schema, "well") ? "ready" : "missing" },
      { title: "Maintenance Queue", detail: "Open work, due dates, asset, owner, and blocker state.", status: screenExists(schema, "maintenance") ? "ready" : "missing" },
      { title: "HSE Evidence", detail: "Permits, inspections, incidents, corrective actions, and attached proof.", status: screenExists(schema, "hse") || screenExists(schema, "permit") ? "ready" : "missing" },
    ];
    outputs = [
      { title: "Field risk board", detail: "Prioritized wells, maintenance, and downtime with source rows.", status: "needs_data" },
      { title: "HSE attention list", detail: "Missing evidence, overdue permits, and corrective actions.", status: "needs_data" },
    ];
    recommendedScreens = [
      { title: "Wells", detail: screenExists(schema, "well") ? "Already present." : "Tracks assets, status, checks, and risk.", status: screenExists(schema, "well") ? "ready" : "missing" },
      { title: "Maintenance Queue", detail: screenExists(schema, "maintenance") ? "Already present." : "Keeps field work, owners, due dates, and blockers visible.", status: screenExists(schema, "maintenance") ? "ready" : "missing" },
      { title: "HSE Evidence", detail: screenExists(schema, "hse") ? "Already present." : "Stores permits, inspections, incidents, and proof.", status: screenExists(schema, "hse") ? "ready" : "missing" },
      { title: "Downtime Log", detail: screenExists(schema, "downtime") ? "Already present." : "Records downtime causes, impact, and next actions.", status: screenExists(schema, "downtime") ? "ready" : "missing" },
    ];
  } else if (isFinance(schema, profile)) {
    currentFocus = [
      { title: "Cash control", detail: "Watch invoices, expenses, payments, and forecast risk with source rows.", status: "watching" },
      { title: "Approval safety", detail: "Keep sensitive money moves behind confirmations, Decisions, and Receipts.", status: "ready" },
    ];
    dataNeeds = [
      { title: "Invoices", detail: "Invoice number, status, amount, due date, owner, and next action.", status: screenExists(schema, "invoice") ? "ready" : "missing" },
      { title: "Expenses", detail: "Expense rows, vendor, category, approval state, and receipt proof.", status: screenExists(schema, "expense") ? "ready" : "missing" },
      { title: "Cash Forecast", detail: "Expected inflows, outflows, confidence, and assumptions.", status: screenExists(schema, "cash") || screenExists(schema, "forecast") ? "ready" : "missing" },
    ];
    outputs = [
      { title: "Cash risk summary", detail: "A table of payment risks, overdue items, and source facts.", status: "needs_data" },
      { title: "30-day forecast", detail: "Projection with confidence and missing assumptions called out.", status: "needs_data" },
    ];
    recommendedScreens = [
      { title: "Finance Dashboard", detail: screenExists(schema, "finance") || screenExists(schema, "dashboard") ? "Already present." : "Shows cash, invoice, expense, and payment health.", status: screenExists(schema, "finance") || screenExists(schema, "dashboard") ? "ready" : "missing" },
      { title: "Invoices", detail: screenExists(schema, "invoice") ? "Already present." : "Tracks payment state, owner, value, and due dates.", status: screenExists(schema, "invoice") ? "ready" : "missing" },
      { title: "Expenses", detail: screenExists(schema, "expense") ? "Already present." : "Captures spend, approvals, and receipts.", status: screenExists(schema, "expense") ? "ready" : "missing" },
      { title: "Cash Forecast", detail: screenExists(schema, "cash") || screenExists(schema, "forecast") ? "Already present." : "Turns projections into a reusable workspace output.", status: screenExists(schema, "cash") || screenExists(schema, "forecast") ? "ready" : "missing" },
    ];
  } else if (isSales(schema, profile)) {
    currentFocus = [
      { title: "Pipeline movement", detail: "Rank leads, deals, proposals, and follow-ups by next action.", status: "watching" },
      { title: "Outreach control", detail: "Draft messages from approved context without sending until the user approves.", status: "ready" },
    ];
    dataNeeds = [
      { title: "Leads", detail: "Name, stage, owner, source, last touch, next action, and value.", status: screenExists(schema, "lead") ? "ready" : "missing" },
      { title: "Deals Pipeline", detail: "Stage, value, probability, blocker, and close date.", status: screenExists(schema, "deal") || screenExists(schema, "pipeline") ? "ready" : "missing" },
      { title: "Outreach Queue", detail: "Warm replies, draft messages, follow-up dates, and approvals.", status: screenExists(schema, "outreach") ? "ready" : "missing" },
    ];
    outputs = [
      { title: "Pipeline triage", detail: "Ranked deals with evidence and next action.", status: "needs_data" },
      { title: "Draft outreach", detail: "Emails, messages, or call notes saved for approval.", status: "needs_data" },
    ];
    recommendedScreens = [
      { title: "Leads", detail: screenExists(schema, "lead") ? "Already present." : "Captures prospects and first qualification facts.", status: screenExists(schema, "lead") ? "ready" : "missing" },
      { title: "Deals Pipeline", detail: screenExists(schema, "deal") || screenExists(schema, "pipeline") ? "Already present." : "Moves opportunities through clear stages.", status: screenExists(schema, "deal") || screenExists(schema, "pipeline") ? "ready" : "missing" },
      { title: "Outreach Queue", detail: screenExists(schema, "outreach") ? "Already present." : "Keeps drafts and follow-ups out of chat drift.", status: screenExists(schema, "outreach") ? "ready" : "missing" },
      { title: "Proposals", detail: screenExists(schema, "proposal") ? "Already present." : "Tracks proposal drafts, status, and approvals.", status: screenExists(schema, "proposal") ? "ready" : "missing" },
    ];
  } else if (isDelivery(schema, profile)) {
    currentFocus = [
      { title: "Delivery blockers", detail: "Find overdue tasks, blocked handoffs, and projects without a next action.", status: "watching" },
      { title: "Execution rhythm", detail: "Turn repeated project updates into assignments, screens, and Decisions.", status: "ready" },
    ];
    recommendedScreens = [
      { title: "Projects", detail: screenExists(schema, "project") ? "Already present." : "Tracks client/internal projects and ownership.", status: screenExists(schema, "project") ? "ready" : "missing" },
      { title: "Tasks", detail: screenExists(schema, "task") ? "Already present." : "Captures the work that moves projects forward.", status: screenExists(schema, "task") ? "ready" : "missing" },
      { title: "Blockers", detail: screenExists(schema, "blocker") ? "Already present." : "Makes stuck work visible before it quietly ages.", status: screenExists(schema, "blocker") ? "ready" : "missing" },
      { title: "Delivery Calendar", detail: screenExists(schema, "calendar") ? "Already present." : "Shows deadlines, handoffs, and review dates.", status: screenExists(schema, "calendar") ? "ready" : "missing" },
    ];
  } else if (isCustomerSuccess(schema, profile)) {
    currentFocus = [
      { title: "Account risk", detail: "Rank accounts, tickets, renewals, and churn signals by evidence.", status: "watching" },
      { title: "Success actions", detail: "Turn follow-ups and save plans into visible workspace actions.", status: "ready" },
    ];
    recommendedScreens = [
      { title: "Accounts", detail: screenExists(schema, "account") ? "Already present." : "Keeps customers, owners, status, and risk visible.", status: screenExists(schema, "account") ? "ready" : "missing" },
      { title: "Tickets", detail: screenExists(schema, "ticket") ? "Already present." : "Connects support work to account health.", status: screenExists(schema, "ticket") ? "ready" : "missing" },
      { title: "Renewal Risks", detail: screenExists(schema, "renewal") ? "Already present." : "Tracks risk, due dates, blockers, and next actions.", status: screenExists(schema, "renewal") ? "ready" : "missing" },
      { title: "Success Plans", detail: screenExists(schema, "success") ? "Already present." : "Stores repeatable plans instead of losing them in chat.", status: screenExists(schema, "success") ? "ready" : "missing" },
    ];
  } else if (isRecruiting(schema, profile)) {
    currentFocus = [
      { title: "Candidate movement", detail: "Rank candidates, interviews, scorecards, and offers by next hiring action.", status: "watching" },
      { title: "Evidence control", detail: "Separate real candidate evidence from missing scorecards or assumptions.", status: "ready" },
    ];
    recommendedScreens = [
      { title: "Candidates", detail: screenExists(schema, "candidate") ? "Already present." : "Tracks candidate facts, status, and owner.", status: screenExists(schema, "candidate") ? "ready" : "missing" },
      { title: "Interview Pipeline", detail: screenExists(schema, "interview") ? "Already present." : "Keeps interviews and stage movement visible.", status: screenExists(schema, "interview") ? "ready" : "missing" },
      { title: "Scorecards", detail: screenExists(schema, "scorecard") ? "Already present." : "Stores evaluation evidence for decisions.", status: screenExists(schema, "scorecard") ? "ready" : "missing" },
      { title: "Offers", detail: screenExists(schema, "offer") ? "Already present." : "Tracks offers, approvals, and next follow-ups.", status: screenExists(schema, "offer") ? "ready" : "missing" },
    ];
  } else if (isPartnerships(schema, profile)) {
    currentFocus = [
      { title: "Partner movement", detail: "Track conversations, obligations, follow-ups, and expansion opportunities.", status: "watching" },
      { title: "Commitment control", detail: "Keep partner promises visible with owners, due dates, and receipts.", status: "ready" },
    ];
    recommendedScreens = [
      { title: "Partners", detail: screenExists(schema, "partner") ? "Already present." : "Keeps partner records and context visible.", status: screenExists(schema, "partner") ? "ready" : "missing" },
      { title: "Partner Pipeline", detail: screenExists(schema, "pipeline") ? "Already present." : "Tracks opportunities and next moves.", status: screenExists(schema, "pipeline") ? "ready" : "missing" },
      { title: "Obligations", detail: screenExists(schema, "obligation") ? "Already present." : "Turns commitments into owned work.", status: screenExists(schema, "obligation") ? "ready" : "missing" },
      { title: "Expansion Opportunities", detail: screenExists(schema, "expansion") ? "Already present." : "Captures where the relationship can grow.", status: screenExists(schema, "expansion") ? "ready" : "missing" },
    ];
  }

  if (recommendedScreens.length === 0) {
    recommendedScreens = schemaCollections.slice(0, 4);
  }

  return {
    currentFocus,
    starterActions,
    dataNeeds,
    skills: skillIds.map((id) => ({
      title: SKILL_LABELS[id] || id.replace(/_/g, " "),
      detail: SKILL_DETAILS[id] || "Loaded when the task calls for it.",
      status: "ready",
    })),
    outputs,
    failedRuns: [
      {
        title: "No failed runs tracked yet",
        detail: "Failures should appear here with the missing data, tool, permission, or skill needed next.",
        status: "ready",
      },
    ],
    recommendedScreens,
    successMetrics: successMetrics.length > 0
      ? successMetrics
      : [
          "Answers use real workspace rows or clearly say data is missing.",
          "Repeatable work becomes a screen, assignment, or Decision.",
          "Failures explain the next smallest unblock step.",
        ],
  };
}
